import { LabelBackgrounds } from 'design-system/components/Sticker';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { Iso8601DateTimeString } from 'lib/types';

export enum ImporterRunStatus {
  // Note: initially create Importer runs should automatically move to 'in progress' upon creation
  Pending = 'pending',
  Running = 'running',
  Errored = 'errored',
  Completed = 'completed',
  Canceled = 'canceled',
  Killed = 'killed',
}

export const IMPORTER_RUN_STATUS_TO_VARIANT: Record<ImporterRunStatus, LabelBackgrounds> = {
  [ImporterRunStatus.Pending]: 'blue',
  [ImporterRunStatus.Running]: 'yellow',
  [ImporterRunStatus.Errored]: 'red',
  [ImporterRunStatus.Completed]: 'green',
  [ImporterRunStatus.Canceled]: 'grey',
  [ImporterRunStatus.Killed]: 'grey',
};

export const IMPORTER_RUN_STATUS_TO_LABEL: Record<ImporterRunStatus, string> = {
  [ImporterRunStatus.Pending]: 'Pending',
  [ImporterRunStatus.Running]: 'In Progress',
  [ImporterRunStatus.Errored]: 'Errored',
  [ImporterRunStatus.Completed]: 'Completed',
  [ImporterRunStatus.Canceled]: 'Canceled',
  [ImporterRunStatus.Killed]: 'Canceled',
};

export enum RowErrorType {
  CreatedAtInvalid = 'created_at_invalid',
  RecordNotFound = 'record_not_found',
  ReferenceIdNotUnique = 'reference_id_not_unique',
  ReferenceIdRequired = 'reference_id_required',
  StatusNotFound = 'status_not_found',
  InvalidField = 'invalid_field',
  InvalidIpAddress = 'invalid_ip_address',
  FileDownloadNotSupported = 'file_download_not_supported',
  FileNotFound = 'file_not_found',
  Unknown = 'unknown',
}

export const ROW_ERROR_TYPE_TO_NAME: Record<RowErrorType, string> = {
  [RowErrorType.CreatedAtInvalid]: 'Invalid created_at value',
  [RowErrorType.RecordNotFound]: 'Record not found',
  [RowErrorType.ReferenceIdNotUnique]: 'Cannot create new object with duplicate reference_id',
  [RowErrorType.ReferenceIdRequired]: 'Reference ID not specified',
  [RowErrorType.StatusNotFound]: 'Status does not exist',
  [RowErrorType.InvalidField]: 'Invalid Field',
  [RowErrorType.InvalidIpAddress]: 'Invalid IP Address',
  [RowErrorType.FileDownloadNotSupported]: 'File Download Not Supported',
  [RowErrorType.FileNotFound]: 'File Not Found',
  [RowErrorType.Unknown]: 'Unknown',
};

export enum ImporterRunGlobalErrorType {
  BadDataSource = 'bad_data_source',
  FailedToConnect = 'failed_to_connect',
  FailedToRead = 'failed_to_read',
  FailedToWrite = 'failed_to_write',
  MaxRowErrorCountExceeded = 'max_row_error_count_exceeded',
}

export const IMPORTER_RUN_GLOBAL_ERROR_TYPE_TO_NAME: Record<ImporterRunGlobalErrorType, string> = {
  [ImporterRunGlobalErrorType.BadDataSource]: 'Bad data source',
  [ImporterRunGlobalErrorType.FailedToConnect]: 'Failed to connect to the server',
  [ImporterRunGlobalErrorType.FailedToRead]: 'Failed to read the file',
  [ImporterRunGlobalErrorType.FailedToWrite]: 'Failed to write the file',
  [ImporterRunGlobalErrorType.MaxRowErrorCountExceeded]: 'Max row error count exceeded',
};

export interface GlobalError {
  'error-type': ImporterRunGlobalErrorType;
  message?: string;
}

export interface ErrorDetail {
  'global-errors': GlobalError[];
}

export interface RunData {
  'column-names': string[];
  'created-count': number;
  'error-count': number;
  'error-details': ErrorDetail;
  'success-count': number;
  'updated-count': number;
  'estimated-total-count': number;
}

export interface ErroredRow {
  'error-type': RowErrorType;
  'error-message': string;
  row: { [column: string]: string };
  'row-number': number;
}

export enum RowAction {
  Created = 'created',
  Updated = 'updated',
}

export interface ImportedRow {
  action: RowAction;
  token: string;
  row: { [column: string]: string };
  'row-number': number;
}

export interface ImporterRunAttributes {
  'created-at': Iso8601DateTimeString;
  'ended-at': Iso8601DateTimeString;
  'error-preview': ErroredRow[];
  'last-synced-at': Iso8601DateTimeString;
  'run-count': number;
  'run-data': RunData;
  'started-at': Iso8601DateTimeString;
  status: ImporterRunStatus;
  'updated-at': Iso8601DateTimeString;
  'success-preview': ImportedRow[];
}

export interface ImporterRunRelationships {
  'importer-pipeline': ApiRelationship<ApiModel.Importer>;
}

export type ImporterRun = ApiResource<
  ImporterRunAttributes,
  ImporterRunRelationships,
  ApiModel.ImporterRun
>;
